import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { config } from '../config';
import { IdentityProvider } from '../generated/id-service-env-administration';
import {
  ChangePassword,
  ChangePasswordCrumb,
} from './components/ChangePassword/ChangePassword';
import { SignIn } from './components/SignIn/SignIn';
import {
  AuthServiceWebHookSecret,
  AuthServiceWebhookSecretCrumb,
  AxAuthProviderDetailCrumb,
  AxAuthServiceConfiguration,
  AxAuthServiceConfigurationCrumb,
  ForgotPasswordWebHookCreate,
  ForgotPasswordWebHookCreateBreadCrumb,
  ForgotPasswordWebHookDetails,
  ForgotPasswordWebHookDetailsBreadCrumb,
  IdpAxAuthDetail,
  MigrationWebHookCreate,
  MigrationWebHookCreateBreadCrumb,
  MigrationWebHookDetails,
  MigrationWebHookDetailsBreadCrumb,
  OAuthClientCreate,
  OAuthClientCreateCrumb,
  OAuthClientDetail,
  OAuthClientDetailCrumb,
  OAuthClients,
  OAuthClientsCrumb,
  UserCreate,
  UserCreateCrumb,
  UserDetail,
  UserDetailCrumb,
  UserResetPassword,
  UserResetPasswordCrumb,
  UserSignUpWebHookCreate,
  UserSignUpWebHookCreateBreadCrumb,
  UserSignUpWebHookDetails,
  UserSignUpWebHookDetailsBreadCrumb,
  UserStoreCreate,
  UserStoreCreateCrumb,
  UserStoreDetail,
  UserStoreDetailCrumb,
  UserStores,
  UserStoresCrumb,
  Users,
  UsersCrumb,
} from './stations/AxAuthService';
import {
  CloudStorageAccountDetails,
  CloudStorageAccountDetailsCrumb,
  ContainerRegistryDetails,
  ContainerRegistryDetailsCrumb,
  ContainerRegistryExplorer,
  ContainerRegistryExplorerCrumb,
  ContainerSasToken,
  ContainerSasTokenCrumb,
  CustomizableServiceCreate,
  CustomizableServiceDeploymentCreate,
  CustomizableServiceDeploymentCreateCrumb,
  CustomizableServiceDeploymentDetails,
  CustomizableServiceDeploymentDetailsCrumb,
  CustomizableServiceDeploymentExplorer,
  CustomizableServiceDeploymentExplorerCrumb,
  CustomizableServiceDetails,
  CustomizableServiceDetailsCrumb,
  CustomizableServices,
  CustomizableServicesCreateCrumb,
  CustomizableServicesCrumb,
  DeploymentManifestDetails,
  DeploymentManifestDetailsCrumb,
  HostingServiceConfiguration,
  HostingServiceConfigurationCrumb,
  PiletArtifactsExplorer,
  PiletArtifactsExplorerCrumb,
  RMQConnectionDetail,
  RMQConnectionDetailCrumb,
  RMQConnectionResetPassword,
  RMQConnectionResetPasswordCrumb,
  ServiceDeploymentManifests,
  ServiceDeploymentManifestsCrumb,
  StorageAccountContainerCreate,
  StorageAccountContainerCreateCrumb,
  StorageAccountContainers,
  StorageAccountContainersCrumb,
  StorageAccountLogs,
  StorageAccountLogsCrumb,
  StorageContainerDetails,
  StorageContainerDetailsCrumb,
} from './stations/HostingService';
import {
  ServiceDeploymentLogs,
  ServiceDeploymentLogsCrumb,
} from './stations/HostingService/ServiceDeploymentLogs/ServiceDeploymentLogs';
import {
  AxinomProviderDetailCrumb,
  AzureAdProviderDetailCrumb,
  EmailWhitelistPatternCreate,
  EmailWhitelistPatternCreateCrumb,
  EmailWhitelistPatternDetail,
  EmailWhitelistPatternDetailCrumb,
  EmailWhitelistPatterns,
  EmailWhitelistPatternsCrumb,
  EnvironmentConnection,
  EnvironmentConnectionCrumb,
  EnvironmentCreate,
  EnvironmentCreateCrumb,
  EnvironmentDetail,
  EnvironmentDetailCrumb,
  Environments,
  EnvironmentsCrumb,
  GoogleProviderDetailCrumb,
  Home,
  IdentityServiceConfiguration,
  IdentityServiceConfigurationCrumb,
  IdpAxinomDetail,
  IdpAzureAdDetail,
  IdpGoogleDetail,
  ServiceAccountCreate,
  ServiceAccountCreateCrumb,
  ServiceAccountDetail,
  ServiceAccountDetailCrumb,
  ServiceAccountGenerateToken,
  ServiceAccountGenerateTokenCrumb,
  ServiceAccountNewClientSecret,
  ServiceAccountNewClientSecretCrumb,
  ServiceAccountPermissions,
  ServiceAccountPermissionsCrumb,
  ServiceAccounts,
  ServiceAccountsCrumb,
  ServicesLogMessages,
  ServicesLogMessagesCrumb,
  TenantAdministratorCreate,
  TenantAdministratorCreateCrumb,
  TenantAdministratorDetail,
  TenantAdministratorDetailCrumb,
  TenantAdministrators,
  TenantAdministratorsCrumb,
} from './stations/IdService';
import {
  IdProvidersCrumb,
  IdentityProviders,
} from './stations/IdService/IdentityProviders/IdentityProviders';
import {
  CustomProviderDetailCrumb,
  IdpCustomDetail,
} from './stations/IdService/IdpCustom/IdpCustom';
import {
  ImageServiceConfiguration,
  ImageServiceConfigurationCrumb,
} from './stations/ImageService/ImageServiceConfiguration/ImageServiceConfiguration';
import {
  LocalizationServiceConfiguration,
  LocalizationServiceConfigurationCrumb,
} from './stations/LocalizationService/LocalizationServiceConfiguration/LocalizationServiceConfiguration';
import {
  MicroFeServiceConfiguration,
  MicroFeServiceConfigurationCrumb,
} from './stations/MicroFeService/MicroFeServiceConfiguration/MicroFeServiceConfiguration';
import {
  PersonalizationServiceConfiguration,
  PersonalizationServiceConfigurationCrumb,
} from './stations/PersonalizationService/PersonalizationServiceConfiguration/PersonalizationServiceConfiguration';

import {
  DrmServiceConfiguration,
  DrmServiceConfigurationCrumb,
} from './stations/DrmService/DrmServiceConfiguration/DrmServiceConfiguration';
import {
  PortalServiceConfiguration,
  PortalServiceConfigurationCrumb,
} from './stations/PortalService/PortalServiceConfiguration/PortalServiceConfiguration';
import {
  UserServiceConfiguration,
  UserServiceConfigurationCrumb,
} from './stations/UserService/UserServiceConfiguration/UserServiceConfiguration';
import {
  VideoServiceConfiguration,
  VideoServiceConfigurationCrumb,
} from './stations/VideoService/VideoServiceConfiguration/VideoServiceConfiguration';

export interface RouteConfig {
  path: string;
  component: React.FC;
  meta?: { breadcrumb: BreadcrumbResolver };
}

const idServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(ManagedServiceId.ID_SERVICE)
    ? [
        { path: '/', component: Home },
        {
          path: '/signIn',
          component: SignIn,
        },
        {
          path: '/changePassword',
          component: ChangePassword,
          meta: { breadcrumb: ChangePasswordCrumb },
        },
        {
          path: `/tenantAdmin`,
          component: TenantAdministrators,
          meta: { breadcrumb: TenantAdministratorsCrumb },
        },
        {
          path: '/tenantAdmin/create',
          component: TenantAdministratorCreate,
          meta: { breadcrumb: TenantAdministratorCreateCrumb },
        },
        {
          path: `/tenantAdmin/:tenantAdminId`,
          component: TenantAdministratorDetail,
          meta: { breadcrumb: TenantAdministratorDetailCrumb },
        },
        {
          path: '/env',
          component: Environments,
          meta: { breadcrumb: EnvironmentsCrumb },
        },
        {
          path: '/env/create',
          component: EnvironmentCreate,
          meta: { breadcrumb: EnvironmentCreateCrumb },
        },
        {
          path: '/env/:envId',
          component: EnvironmentDetail,
          meta: { breadcrumb: EnvironmentDetailCrumb },
        },
        {
          path: '/env/:envId/serviceAccounts',
          component: ServiceAccounts,
          meta: { breadcrumb: ServiceAccountsCrumb },
        },
        {
          path: `/env/:envId/serviceAccounts/create`,
          component: ServiceAccountCreate,
          meta: { breadcrumb: ServiceAccountCreateCrumb },
        },
        {
          path: `/env/:envId/serviceAccounts/:id/clientSecret`,
          component: ServiceAccountNewClientSecret,
          meta: { breadcrumb: ServiceAccountNewClientSecretCrumb },
        },
        {
          path: `/env/:envId/serviceAccounts/:id/generateToken`,
          component: ServiceAccountGenerateToken,
          meta: { breadcrumb: ServiceAccountGenerateTokenCrumb },
        },
        {
          path: `/env/:envId/serviceAccounts/:id/permissions`,
          component: ServiceAccountPermissions,
          meta: { breadcrumb: ServiceAccountPermissionsCrumb },
        },
        {
          path: `/env/:envId/serviceAccounts/:id`,
          component: ServiceAccountDetail,
          meta: { breadcrumb: ServiceAccountDetailCrumb },
        },

        {
          path: '/env/:envId/servicesLogMessages',
          component: ServicesLogMessages,
          meta: { breadcrumb: ServicesLogMessagesCrumb },
        },
        {
          path: '/env/:envId/environmentConnection',
          component: EnvironmentConnection,
          meta: { breadcrumb: EnvironmentConnectionCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config`,
          component: IdentityServiceConfiguration,
          meta: { breadcrumb: IdentityServiceConfigurationCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders/${IdentityProvider.Axinom}`,
          component: IdpAxinomDetail,
          meta: { breadcrumb: AxinomProviderDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders/${IdentityProvider.AxAuth}`,
          component: IdpAxAuthDetail,
          meta: { breadcrumb: AxAuthProviderDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders/${IdentityProvider.AzureAd}`,
          component: IdpAzureAdDetail,
          meta: { breadcrumb: AzureAdProviderDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders/${IdentityProvider.Google}`,
          component: IdpGoogleDetail,
          meta: { breadcrumb: GoogleProviderDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders/${IdentityProvider.Custom}`,
          component: IdpCustomDetail,
          meta: { breadcrumb: CustomProviderDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/identityProviders`,
          component: IdentityProviders,
          meta: { breadcrumb: IdProvidersCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns`,
          component: EmailWhitelistPatterns,
          meta: { breadcrumb: EmailWhitelistPatternsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns/create`,
          component: EmailWhitelistPatternCreate,
          meta: { breadcrumb: EmailWhitelistPatternCreateCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns/:emailWhitelistPatternId`,
          component: EmailWhitelistPatternDetail,
          meta: { breadcrumb: EmailWhitelistPatternDetailCrumb },
        },
      ]
    : [];

const portalServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(
    ManagedServiceId.PORTAL_SERVICE,
  )
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.PORTAL_SERVICE}/config`,
          component: PortalServiceConfiguration,
          meta: { breadcrumb: PortalServiceConfigurationCrumb },
        },
      ]
    : [];

const microFeServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(
    ManagedServiceId.MICRO_FRONTEND_SERVICE,
  )
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.MICRO_FRONTEND_SERVICE}/config`,
          component: MicroFeServiceConfiguration,
          meta: { breadcrumb: MicroFeServiceConfigurationCrumb },
        },
      ]
    : [];

const imageServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(ManagedServiceId.IMAGE_SERVICE)
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.IMAGE_SERVICE}/config`,
          component: ImageServiceConfiguration,
          meta: { breadcrumb: ImageServiceConfigurationCrumb },
        },
      ]
    : [];

const videoServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(ManagedServiceId.VIDEO_SERVICE)
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.VIDEO_SERVICE}/config`,
          component: VideoServiceConfiguration,
          meta: { breadcrumb: VideoServiceConfigurationCrumb },
        },
      ]
    : [];

const personalizationServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(
    ManagedServiceId.PERSONALIZATION_SERVICE,
  )
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.PERSONALIZATION_SERVICE}/config`,
          component: PersonalizationServiceConfiguration,
          meta: { breadcrumb: PersonalizationServiceConfigurationCrumb },
        },
      ]
    : [];

const drmServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(ManagedServiceId.DRM_SERVICE)
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.DRM_SERVICE}/config`,
          component: DrmServiceConfiguration,
          meta: { breadcrumb: DrmServiceConfigurationCrumb },
        },
      ]
    : [];

const axAuthServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(
    ManagedServiceId.AXAUTH_SERVICE,
  )
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config`,
          component: AxAuthServiceConfiguration,
          meta: { breadcrumb: AxAuthServiceConfigurationCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores`,
          component: UserStores,
          meta: { breadcrumb: UserStoresCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/create`,
          component: UserStoreCreate,
          meta: { breadcrumb: UserStoreCreateCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId`,
          component: UserStoreDetail,
          meta: { breadcrumb: UserStoreDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/provisionedUsers`,
          component: Users,
          meta: { breadcrumb: UsersCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/provisionedUsers/create`,
          component: UserCreate,
          meta: { breadcrumb: UserCreateCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/provisionedUsers/:userId`,
          component: UserDetail,
          meta: { breadcrumb: UserDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/provisionedUsers/:userId/resetPassword`,
          component: UserResetPassword,
          meta: { breadcrumb: UserResetPasswordCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/oAuthClients`,
          component: OAuthClients,
          meta: { breadcrumb: OAuthClientsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/oAuthClients/create`,
          component: OAuthClientCreate,
          meta: { breadcrumb: OAuthClientCreateCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/oAuthClients/:clientId`,
          component: OAuthClientDetail,
          meta: { breadcrumb: OAuthClientDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/userSignUp/create`,
          component: UserSignUpWebHookCreate,
          meta: { breadcrumb: UserSignUpWebHookCreateBreadCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/userSignUp/details`,
          component: UserSignUpWebHookDetails,
          meta: { breadcrumb: UserSignUpWebHookDetailsBreadCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/userSignUp/details/secret/:hookType`,
          component: AuthServiceWebHookSecret,
          meta: { breadcrumb: AuthServiceWebhookSecretCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/forgotPassword/create`,
          component: ForgotPasswordWebHookCreate,
          meta: { breadcrumb: ForgotPasswordWebHookCreateBreadCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/forgotPassword/details`,
          component: ForgotPasswordWebHookDetails,
          meta: { breadcrumb: ForgotPasswordWebHookDetailsBreadCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/forgotPassword/details/secret/:hookType`,
          component: AuthServiceWebHookSecret,
          meta: { breadcrumb: AuthServiceWebhookSecretCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/migration/create`,
          component: MigrationWebHookCreate,
          meta: { breadcrumb: MigrationWebHookCreateBreadCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/migration/details`,
          component: MigrationWebHookDetails,
          meta: { breadcrumb: MigrationWebHookDetailsBreadCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/:userStoreId/migration/details/secret/:hookType`,
          component: AuthServiceWebHookSecret,
          meta: { breadcrumb: AuthServiceWebhookSecretCrumb },
        },
      ]
    : [];

const userServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(ManagedServiceId.USER_SERVICE)
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.USER_SERVICE}/config`,
          component: UserServiceConfiguration,
          meta: { breadcrumb: UserServiceConfigurationCrumb },
        },
      ]
    : [];

const hostingServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(
    ManagedServiceId.HOSTING_SERVICE,
  )
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config`,
          component: HostingServiceConfiguration,
          meta: { breadcrumb: HostingServiceConfigurationCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/storageAccountLogs`,
          component: StorageAccountLogs,
          meta: { breadcrumb: StorageAccountLogsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage`,
          component: CloudStorageAccountDetails,
          meta: { breadcrumb: CloudStorageAccountDetailsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/:storageType/:storageAccountName/containers`,
          component: StorageAccountContainers,
          meta: { breadcrumb: StorageAccountContainersCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/:storageType/:storageAccountName/containers/create`,
          component: StorageAccountContainerCreate,
          meta: { breadcrumb: StorageAccountContainerCreateCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/:storageType/:storageAccountName/containers/:containerName`,
          component: StorageContainerDetails,
          meta: { breadcrumb: StorageContainerDetailsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/:storageType/:storageAccountName/containers/:containerName/sas`,
          component: ContainerSasToken,
          meta: { breadcrumb: ContainerSasTokenCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/rabbitmq`,
          component: RMQConnectionDetail,
          meta: { breadcrumb: RMQConnectionDetailCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/rabbitmq/resetPassword`,
          component: RMQConnectionResetPassword,
          meta: { breadcrumb: RMQConnectionResetPasswordCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services`,
          component: CustomizableServices,
          meta: { breadcrumb: CustomizableServicesCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/create`,
          component: CustomizableServiceCreate,
          meta: { breadcrumb: CustomizableServicesCreateCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId`,
          component: CustomizableServiceDetails,
          meta: { breadcrumb: CustomizableServiceDetailsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/logs`,
          component: ServiceDeploymentLogs,
          meta: { breadcrumb: ServiceDeploymentLogsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/containerRegistry`,
          component: ContainerRegistryDetails,
          meta: { breadcrumb: ContainerRegistryDetailsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/deployments`,
          component: CustomizableServiceDeploymentExplorer,
          meta: { breadcrumb: CustomizableServiceDeploymentExplorerCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/deployments/create`,
          component: CustomizableServiceDeploymentCreate,
          meta: { breadcrumb: CustomizableServiceDeploymentCreateCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/deployments/:serviceDeploymentId`,
          component: CustomizableServiceDeploymentDetails,
          meta: { breadcrumb: CustomizableServiceDeploymentDetailsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/deployments/:serviceDeploymentId/logs`,
          component: ServiceDeploymentLogs,
          meta: { breadcrumb: ServiceDeploymentLogsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/deployment-manifests`,
          component: ServiceDeploymentManifests,
          meta: { breadcrumb: ServiceDeploymentManifestsCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/containerImages`,
          component: ContainerRegistryExplorer,
          meta: { breadcrumb: ContainerRegistryExplorerCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/piletArtifacts`,
          component: PiletArtifactsExplorer,
          meta: { breadcrumb: PiletArtifactsExplorerCrumb },
        },
        {
          path: `/env/:envId/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/:customizableServiceId/deployment-manifests/:deploymentManifestId`,
          component: DeploymentManifestDetails,
          meta: { breadcrumb: DeploymentManifestDetailsCrumb },
        },
      ]
    : [];

const localizationServiceRoutes: RouteConfig[] =
  config.managedServicesAvailableForUse.includes(
    ManagedServiceId.LOCALIZATION_SERVICE,
  )
    ? [
        {
          path: `/env/:envId/services/${ManagedServiceId.LOCALIZATION_SERVICE}/config`,
          component: LocalizationServiceConfiguration,
          meta: { breadcrumb: LocalizationServiceConfigurationCrumb },
        },
      ]
    : [];

export const Routes: RouteConfig[] = [
  ...idServiceRoutes,
  ...portalServiceRoutes,
  ...axAuthServiceRoutes,
  ...userServiceRoutes,
  ...hostingServiceRoutes,
  ...microFeServiceRoutes,
  ...imageServiceRoutes,
  ...videoServiceRoutes,
  ...personalizationServiceRoutes,
  ...localizationServiceRoutes,
  ...drmServiceRoutes,
];
